/**
 * DEPO FABRİKASI — ortam değişkenine bakıp doğru uygulamayı seçer.
 *
 * KURAL: Sunucu adresi varsa Worker, yoksa yerel depo.
 *
 * Bu tek dosya, uygulamanın geri kalanının "sunucu var mı yok mu"
 * diye düşünmesini engeller. Bileşenler yalnızca `Repository` arayüzünü görür.
 *
 * Ayrıca YEREL VERİYİ Worker'a TAŞIMA yolu sunar: kullanıcı adresi girdiğinde
 * mevcut localStorage karakterleri otomatik olarak yüklenir. Bu geçiş olmadan
 * Worker'a geçmek "karakterlerim kayboldu" demek olurdu.
 */

import { type ContentPack, validateContentPack } from '@tttrpg/rules'
import type { CharacterRow, Repository } from './contract'
import { createLocalRepository } from './local'
import { createWorkerRepository } from './remote'

/** Ortam değişkeni okuma — Vite `import.meta.env` ile sağlar. */
export interface RepositoryEnv {
  /** Worker temel adresi. Boşsa yerel mod. */
  apiUrl?: string | undefined
  /** Tanılama günlüğü. */
  debug?: boolean | undefined
}

export interface FactoryResult {
  repository: Repository
  /** Sunucuya bağlanılamadıysa neden? (yerel moda düşme sebebi) */
  fallbackReason?: string
}

/**
 * Depoyu oluşturur.
 *
 * @param env      `import.meta.env` nesnesi (veya elle yazılmış test objesi)
 * @param previous Önceki depo. Varsa ve sunucu moduna geçiliyorsa, yerel
 *                 veriyi sunucuya taşımak için kullanılır.
 */
export async function createRepository(
  env: RepositoryEnv,
  previous?: Repository,
): Promise<FactoryResult> {
  const apiUrl = (env.apiUrl ?? '').trim()

  if (!apiUrl) {
    return { repository: previous ?? createLocalRepository() }
  }

  const worker = createWorkerRepository({ baseUrl: apiUrl, debug: env.debug === true })

  // Adres tanımlı ama Worker ayakta mı? Yoksa kullanıcı verisini
  // kaybetmemek için yerel moda düşüyoruz.
  try {
    await worker.getAuth()
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'bilinmeyen hata'
    return {
      repository: previous ?? createLocalRepository(),
      fallbackReason: `Sunucuya ulaşılamadı (${reason}). Yerel modda çalışılıyor.`,
    }
  }

  const local = previous?.backend === 'local' ? previous : null
  if (local) {
    // Taşıma bir KOLAYLIK özelliği. Başarısız olsa bile uygulama açılmalı;
    // aksi halde "verilerimi yedekleyemiyorum" hatası kullanıcıyı
    // uygulamadan tamamen kapatır.
    try {
      await migrateLocalToWorker(local, worker)
    } catch {
      // Yoksay: kullanıcı yerel verisi hâlâ tarayıcısında duruyor.
    }
  }

  return { repository: worker }
}

// ---------------------------------------------------------------------------
// YEREL → WORKER TAŞIMA
// ---------------------------------------------------------------------------

/**
 * Yerel karakterleri sunucuya yükler.
 *
 * ⚠️ KURAR KURAR: Aynı adlı iki karakter iki ayrı kayda dönüşebilir.
 * Bu yüzden taşıma bir kez yapılır (anahtar `zarhane:v1:taşindi`), ve
 * sunucuda aynı isimli bir karakter varsa ATLANIR — sessizce üzerine
 * yazmaktansa kullanıcı karar versin.
 */
const MIGRATED_KEY = 'zarhane:v1:tasindi'

export async function migrateLocalToWorker(
  local: Repository,
  worker: Repository,
): Promise<{ imported: number; skipped: number }> {
  let imported = 0
  let skipped = 0

  try {
    if (localStorage.getItem(MIGRATED_KEY) === '1') return { imported, skipped }
  } catch {
    return { imported, skipped }
  }

  const [characters, packs] = await Promise.all([
    safe(() => local.listCharacters()),
    safe(() => local.listPacks()),
  ])

  const existing = await safe(() => worker.listCharacters())
  const existingNames = new Set((existing ?? []).map((c) => c.name))

  for (const character of characters ?? []) {
    // Aynı isim zaten varsa: üzerine yazma, atla.
    if (existingNames.has(character.name)) {
      skipped++
      continue
    }
    try {
      await worker.createCharacter(character)
      existingNames.add(character.name)
      imported++
    } catch {
      skipped++
    }
  }

  for (const pack of packs ?? []) {
    const check = validateContentPack(pack)
    if (!check.ok) {
      skipped++
      continue
    }
    try {
      await worker.savePack(pack, { isPublic: false })
      imported++
    } catch {
      skipped++
    }
  }

  // ⚠️ Zar günlüğü TAŞINMAZ. Gerekçesi: her kayıt bir karakter kimliğine
  // bağlıdır; sunucuda bu karakterler YENİ kimlikler alır, eski kimlikler
  // geçersizleşir. Günlüğü taşımanın tek anlamlı yolu, taşımadan sonra
  // isim eşleştirmesiyle yeniden bağlamaktır — o da sessizce yanlış
  // bağlama riski taşır. Kayıp bir atış günlüğü, yanlış bağlanmış bir günlükten
  // iyidir.

  try {
    localStorage.setItem(MIGRATED_KEY, '1')
  } catch {
    /* yoksay */
  }

  return { imported, skipped }
}

/** Hata halinde varsayılan döndüren sarmalayıcı — taşıma asla uygulamayı düşürmez. */
async function safe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn()
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// YARDIMCI
// ---------------------------------------------------------------------------

/** Test/CLI için bellek içi depo. */
export function createMemoryRepository(seed: Partial<Repository> = {}): Repository {
  const characters: CharacterRow[] = []
  const packs: ContentPack[] = []

  const base = createLocalRepository()

  return {
    ...base,
    ...seed,
    backend: 'local',
    async listCharacters() {
      return [...characters]
    },
    async getCharacter(id) {
      return characters.find((c) => c.id === id) ?? null
    },
    async createCharacter(row) {
      const created: CharacterRow = {
        ...row,
        id: crypto.randomUUID(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      characters.push(created)
      return created
    },
    async updateCharacter(id, patch) {
      const index = characters.findIndex((c) => c.id === id)
      if (index < 0) throw new Error('Karakter bulunamadı.')
      const updated = { ...characters[index]!, ...patch, id, updatedAt: Date.now() }
      characters[index] = updated
      return updated
    },
    async deleteCharacter(id) {
      const index = characters.findIndex((c) => c.id === id)
      if (index >= 0) characters.splice(index, 1)
    },
    async listPacks() {
      return [...packs]
    },
    async savePack(pack) {
      const index = packs.findIndex((p) => p.id === pack.id)
      if (index < 0) packs.push(pack)
      else packs[index] = pack
      return pack
    },
    async deletePack(packId) {
      const index = packs.findIndex((p) => p.id === packId)
      if (index >= 0) packs.splice(index, 1)
    },
  }
}

export type {
  AuthState,
  Backend,
  CantripEntry,
  CharacterRow,
  InviteSummary,
  PoolDef,
  PreparedSpellEntry,
  Repository,
  Role,
  RollLogEntry,
  SessionState,
} from './contract'
export { createLocalRepository } from './local'
export { createWorkerRepository, WorkerError } from './remote'
